import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  COMPANY,
  computeInvoiceTotals,
  formatDisplayDate,
  formatMoney,
  lineAmount,
} from './invoiceModelDefaults';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildLineRowsHtml(model) {
  return (model.lineItems || [])
    .map((item) => {
      const qty = item.quantity === '' || item.quantity == null ? '—' : item.quantity;
      return `
        <tr>
          <td>${escapeHtml(item.description || '—')}</td>
          <td style="text-align:center;">${escapeHtml(qty)}</td>
          <td style="text-align:right;">${formatMoney(item.unitPrice, model.currency).replace('₹', '')}</td>
          <td style="text-align:right;">${formatMoney(lineAmount(item), model.currency).replace('₹', '')}</td>
        </tr>
      `;
    })
    .join('');
}

export function buildInvoiceWordHtml(model) {
  const { subtotal, taxAmount, total, gstPercent } = computeInvoiceTotals(model);
  const logoUrl = `${window.location.origin}${COMPANY.logoSrc}`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${escapeHtml(model.invoiceNumber)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #1f2937; margin: 24px; }
    .header { display: table; width: 100%; margin-bottom: 24px; }
    .header-left, .header-right { display: table-cell; vertical-align: top; }
    .header-right { text-align: right; }
    .brand { display: flex; align-items: center; gap: 12px; }
    .brand img { height: 48px; }
    .brand-name { font-size: 22px; font-weight: 700; letter-spacing: 0.04em; }
    .invoice-title { font-size: 28px; font-weight: 700; margin: 0; }
    .tax-label { font-size: 12px; color: #64748b; margin-top: 4px; }
    .meta-grid { display: table; width: 100%; margin: 20px 0; }
    .meta-left, .meta-right { display: table-cell; vertical-align: top; width: 50%; }
    .meta-right { text-align: right; font-size: 13px; line-height: 1.8; }
    .summary { display: table; width: 100%; border-collapse: collapse; margin: 16px 0 24px; }
    .summary div { display: table-cell; padding: 10px 12px; font-size: 12px; font-weight: 700; color: #fff; }
    .summary .gold { background: #e8a838; }
    .summary .dark { background: #1f2937; text-align: right; }
    table.items { width: 100%; border-collapse: collapse; margin-top: 8px; }
    table.items th, table.items td { border-bottom: 1px solid #e5e7eb; padding: 10px 8px; font-size: 13px; }
    table.items th { text-align: left; color: #64748b; font-weight: 600; }
    .totals { width: 280px; margin-left: auto; margin-top: 16px; font-size: 13px; }
    .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
    .totals .grand { font-weight: 700; font-size: 15px; border-top: 1px solid #cbd5e1; margin-top: 8px; padding-top: 8px; }
    .signature { margin-top: 36px; font-size: 13px; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #64748b; text-align: center; line-height: 1.8; }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <div class="brand">
        <img src="${logoUrl}" alt="${escapeHtml(COMPANY.name)}" />
        <div class="brand-name">${escapeHtml(COMPANY.name)}</div>
      </div>
    </div>
    <div class="header-right">
      <h1 class="invoice-title">Invoice ${escapeHtml(model.invoiceNumber)}</h1>
      <div class="tax-label">TAX INVOICE</div>
    </div>
  </div>

  <div class="meta-grid">
    <div class="meta-left">
      <strong>Bill To</strong><br />
      ${escapeHtml(model.billToName || 'Your Client')}<br />
      ${escapeHtml(model.billToAddress || 'Client address').replace(/\n/g, '<br />')}
    </div>
    <div class="meta-right">
      <div><strong>Issue date</strong> ${formatDisplayDate(model.issueDate)}</div>
      <div><strong>Due date</strong> ${formatDisplayDate(model.dueDate)}</div>
      <div><strong>Reference</strong> ${escapeHtml(model.reference || model.invoiceNumber)}</div>
    </div>
  </div>

  <div class="summary">
    <div class="gold">Invoice No.<br />${escapeHtml(model.invoiceNumber)}</div>
    <div class="gold">Issue date<br />${formatDisplayDate(model.issueDate)}</div>
    <div class="gold">Due date<br />${formatDisplayDate(model.dueDate)}</div>
    <div class="dark">Total due (${escapeHtml(model.currency)})<br />${formatMoney(total, model.currency)}</div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align:center;">Quantity</th>
        <th style="text-align:right;">Unit price (${model.currency === 'INR' ? '₹' : model.currency})</th>
        <th style="text-align:right;">Amount (${model.currency === 'INR' ? '₹' : model.currency})</th>
      </tr>
    </thead>
    <tbody>${buildLineRowsHtml(model)}</tbody>
  </table>

  <div class="totals">
    <div><span>Subtotal</span><span>${formatMoney(subtotal, model.currency)}</span></div>
    <div><span>GST ${gstPercent}%</span><span>${formatMoney(taxAmount, model.currency)}</span></div>
    <div class="grand"><span>Total (${model.currency})</span><span>${formatMoney(total, model.currency)}</span></div>
  </div>

  <div class="signature">
    <div>Issuer's signature</div>
    <div style="margin-top: 28px; font-family: 'Segoe Script', cursive; font-size: 18px;">
      ${escapeHtml(model.issuerSignature || '')}
    </div>
  </div>

  <div class="footer">
    ${escapeHtml(model.companyPhone)} &nbsp;|&nbsp; ${escapeHtml(model.companyWebsite)} &nbsp;|&nbsp; ${escapeHtml(model.companyEmail)}<br />
    ${escapeHtml(model.companyAddress)}
  </div>
</body>
</html>`;
}

export async function downloadInvoicePdf(element, fileName) {
  if (!element) throw new Error('Invoice preview not ready');

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const imgHeight = (canvas.height * contentWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;
  const imgData = canvas.toDataURL('image/png');

  pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight + margin;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
    heightLeft -= pageHeight - margin * 2;
  }

  pdf.save(fileName);
}

export function downloadInvoiceWord(model, fileName) {
  const htmlContent = buildInvoiceWordHtml(model);
  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function invoiceFileBaseName(model) {
  const safeNumber = String(model.invoiceNumber || 'invoice').replace(/[^\w-]+/g, '_');
  return `Spotless_AI_Invoice_${safeNumber}`;
}
